import type { ObjectId } from '@freightview/object-id'

import type { DTO } from '../core/dto.ts'
import {
  vBoolean,
  vNonEmptyString,
  vObject,
  vObjectId,
  vOptional,
  vOptionalDef,
  type vOutput,
  vPicklist,
  vString,
  vStringToNumber,
} from '../validation/index.ts'
import type {
  MessageDeliveryStatus,
  MessageSubType,
  MessageType,
} from './common.ts'
import type { LoadStatus } from './load.ts'

export const messageParticipantRoles = [
  'shipper',
  'carrier',
  'system',
  'other',
] as const
export type MessageParticipantRole = (typeof messageParticipantRoles)[number]

export interface MessageParticipant {
  id?: string
  email?: string
  role: MessageParticipantRole
  name?: string
  deliveryStatus?: MessageDeliveryStatus
  isAuthor?: boolean
  carrierId?: string
  carrierName?: string
  readDate?: Date
  archivedDate?: Date
  ipAddress?: string
}
export interface Message {
  _id: ObjectId
  accountId: string
  quoteRequestId: string
  loadId: string
  loadStatus?: LoadStatus
  createdDate: Date
  participants: MessageParticipant[]
  messageText: string
  type: MessageType
  subType: MessageSubType
  doNotNotify?: boolean
  email?: {
    subject: string
    content: string
  }
  _m?: number
}

export interface MessageSummary {
  lastCarrierMessageDate?: Date
  lastShipperMessageDate?: Date
  lastReadDate?: Date
}

export const messageFilterSchema = vObject({
  carrierId: vOptional(vObjectId()),
  search: vOptional(vString()),
  status: vPicklist(['all', 'quoting', 'booked']),
  unreadOnly: vOptional(vBoolean()),
  includeArchived: vOptional(vBoolean()),
})
export type MessageFilterOpts = vOutput<typeof messageFilterSchema>

export const listMessageSchema = vObject({
  ...messageFilterSchema.entries,
  continuationToken: vOptional(vString()),
  limit: vOptionalDef(vStringToNumber(), 20),
})
export type MessageListOpts = vOutput<typeof listMessageSchema>

export const addMessageSchema = vObject({
  messageText: vString(),
  carrierId: vOptional(vObjectId()),
  userId: vOptional(vObjectId()),
})
export type AddMessageDTO = vOutput<typeof addMessageSchema>

export const addCarrierMessageSchema = vObject({
  messageText: vNonEmptyString(),
})
export type AddCarrierMessageDTO = vOutput<typeof addCarrierMessageSchema>

export type MessageInboxItem = Message & {
  readDate?: Date
  archivedDate?: Date
  identifier: MessageThreadIdentifier
}

export const updateThreadReadStatusSchema = vObject({
  read: vBoolean(),
})
export type UpdateThreadReadStatusDTO = vOutput<
  typeof updateThreadReadStatusSchema
>

export interface UnreadCountResponse {
  quoting: number
  booked: number
}

export type MessageThreadIdentifier = {
  loadId: string
  carrierId?: string
  userId: string
}
export const getShipperMessageThreadIdentifier = <
  T extends Message | DTO<Message>,
>(
  message: T,
): MessageThreadIdentifier => {
  let carrierId: string | undefined
  let userId: string
  for (const participant of message.participants) {
    // using a loop to reduce the number of iterations since this could potentially be called a lot
    if (participant.role === 'carrier') {
      // right now this is only used for shipper messages
      userId = participant.id!
      carrierId = participant.carrierId
      if (userId && carrierId) {
        break
      }
    }
  }
  return {
    loadId: message.loadId,
    carrierId,
    userId,
  }
}

export const convertMessageToShipperInboxItem = <
  T extends Message | DTO<Message>,
  TReturn = T extends DTO<Message> ? DTO<MessageInboxItem> : MessageInboxItem,
>(
  userId: string,
  message: T,
): TReturn => {
  const self = message.participants.find(p => p.id === userId)

  return {
    ...message,
    readDate: self?.readDate,
    archivedDate: self?.archivedDate,
    identifier: getShipperMessageThreadIdentifier(message),
  } as TReturn
}

export const compareThreadIdentifiers = (
  a: MessageThreadIdentifier,
  b: MessageThreadIdentifier,
) => {
  return (
    a.loadId === b.loadId &&
    (a.carrierId === b.carrierId || a.userId === b.userId)
  )
}
